/* --------------------------- Design states start -------------------------- */
import {
  Autocomplete,
  type AutocompleteRenderOptionState,
  FormControl,
  FormHelperText,
  ListItem,
  ListItemText,
  TextField,
} from '@mui/material'
/* ---------------------------- Design states end --------------------------- */

/* ------------------------ Functional imports start ------------------------ */
import React, { type HTMLAttributes, type ReactElement, useCallback, useEffect, useState } from "react";
/* ------------------------- Functional imports end ------------------------- */

type Props = {
  id?: string
  value: string
  label: string
  labelWidth?: number
  onChange: (value: string) => void
  validation?: boolean
  errorMessage?: string
  disabled?: boolean
  required?: boolean
  multiline?: boolean
  showError?: boolean
  loadSuggestions?: () => Promise<
    { value: string | number | boolean | null; label?: string; disabled?: boolean }[]
  >
  suggestions?: { value: string | number | boolean | null; label?: string; disabled?: boolean }[]
  // Autocomplete hook support (new pattern)
  useAutocomplete?: boolean
  autocompleteHook?: {
    suggestions: Array<{ id: string; name: string } | { value: string; label: string }>
    isLoading: boolean
    updateQuery: (query: string) => void
    query: string
  }
  // Legacy: Elasticsearch autocomplete support (deprecated, use autocompleteHook instead)
  useElasticAutocomplete?: boolean
  elasticAutocompleteHook?: {
    suggestions: any[]
    isSearching: boolean
    updateTerm: (value: string) => void
    error: Error | null
  }
}

/* -------------------------------------------------------------------------- */
/*                               Start Component                              */
/* -------------------------------------------------------------------------- */
export default React.memo(function TextInput(props: Props): ReactElement {
  /* -------------------------- Non state data start -------------------------- */
  const {
    id,
    label,
    value = '',
    onChange,
    disabled = false,
    required = false,
    errorMessage = '',
    validation,
    multiline,
    showError = true,
    loadSuggestions,
    useAutocomplete = false,
    autocompleteHook,
    useElasticAutocomplete = false,
    elasticAutocompleteHook,
  } = props
  /* --------------------------- Non state data end --------------------------- */
  /* ---------------------------- Flag states start --------------------------- */
  const [error, setError] = useState<boolean>(showError)
  const [suggestions, setSuggestions] = useState<any[]>(props.suggestions || [])
  /* ----------------------------- Flag states end ---------------------------- */

  /* ------------------------------ Effects start ----------------------------- */
  useEffect(() => {
    if (showError) {
      validation !== undefined && value === '' ? setError(true) : setError(false)
    }
  }, [showError, validation, value])

  // Convert autocomplete suggestions to options format
  useEffect(() => {
    if (useAutocomplete && autocompleteHook) {
      const convertedOptions = autocompleteHook.suggestions.map(suggestion => {
        // Handle both {id, name} and {value, label} formats
        if ('id' in suggestion && 'name' in suggestion) {
          return { value: suggestion.name, label: suggestion.name }
        }
        return suggestion as { value: string; label: string }
      })
      setSuggestions(convertedOptions)
    }
  }, [useAutocomplete, autocompleteHook?.suggestions])

  /* ------------------------------- Effects end ------------------------------ */

  const handleOpen = useCallback(async () => {
    // If using autocomplete, trigger initial load of popular items
    if (useAutocomplete && autocompleteHook) {
      // Trigger query with empty string to load popular items
      if (autocompleteHook.query === '') {
        autocompleteHook.updateQuery('')
      }
    }
    // Legacy: Load suggestions if not using autocomplete
    else if (!useAutocomplete && props.suggestions) {
      setSuggestions(props.suggestions)
    }
    else if (!useAutocomplete && loadSuggestions) {
      const suggestions = await loadSuggestions()
      if (suggestions.length > 0) {
        setSuggestions(suggestions)
      }
    }
  }, [loadSuggestions, props.suggestions, useAutocomplete, autocompleteHook])

  const handleChange = (e: any, value: any) => {
    if (value && value.label) {
      onChange(value.label)
    } else if (value === null) {
      onChange('')
    } else if (value) {
      onChange(value)
    }
  }

  const handleInputChange = (e: any, value: string) => {
    // Update autocomplete if enabled (new pattern)
    if (useAutocomplete && autocompleteHook) {
      autocompleteHook.updateQuery(value)
    }
    // Legacy: Update Elasticsearch autocomplete if enabled
    else if (useElasticAutocomplete && elasticAutocompleteHook) {
      elasticAutocompleteHook.updateTerm(value)
    }
    // Also handle the regular change
    handleChange(e, value)
  }

  // Use new autocomplete pattern if available, otherwise fall back to legacy or regular suggestions
  const activeSuggestions = useAutocomplete && autocompleteHook
    ? suggestions
    : useElasticAutocomplete && elasticAutocompleteHook
    ? elasticAutocompleteHook.suggestions
    : suggestions

  const isLoading = useAutocomplete && autocompleteHook
    ? autocompleteHook.isLoading
    : useElasticAutocomplete && elasticAutocompleteHook
    ? elasticAutocompleteHook.isSearching
    : false

  /* ------------------------- Render constants start ------------------------- */
  const renderOption = (
    props: HTMLAttributes<HTMLLIElement>,
    option: any,
    state: AutocompleteRenderOptionState
  ) => {
    return (
      <ListItem {...props} key={option.value} style={{ display: 'flex', alignItems: 'center' }}>
        <ListItemText primary={option.label} />
      </ListItem>
    )
  }
  /* -------------------------- Render constants end -------------------------- */

  /* -------------------------------------------------------------------------- */
  /*                              Render component                              */
  /* -------------------------------------------------------------------------- */
  return (
    <FormControl fullWidth variant="outlined">
      {(loadSuggestions || props.suggestions || useAutocomplete || useElasticAutocomplete) && (
        <Autocomplete
          id={id || label}
          value={value}
          onChange={handleChange}
          onInputChange={handleInputChange}
          freeSolo
          disabled={disabled}
          onOpen={handleOpen}
          openOnFocus
          options={activeSuggestions}
          loading={isLoading}
          renderOption={renderOption}
          renderInput={params => (
            <TextField
              {...params}
              label={label}
              error={error}
              required={required}
              helperText={
                elasticAutocompleteHook?.error
                  ? `Search error: ${elasticAutocompleteHook.error.message}`
                  : undefined
              }
              inputProps={{
                ...params.inputProps,
              }}
            />
          )}
        />
      )}
      {!(loadSuggestions || props.suggestions || useAutocomplete || useElasticAutocomplete) && (
        <TextField
          id={id || label}
          variant="outlined"
          multiline={multiline}
          rows={multiline ? 4 : 1}
          label={label}
          value={value || ''}
          onChange={e => {
            e.currentTarget.value !== '' || validation === undefined
              ? setError(false)
              : setError(true)
            onChange(e.currentTarget.value)
          }}
          autoComplete="text"
          required={required}
          disabled={disabled}
          error={error}
          aria-describedby="text-input"
          inputProps={{
            'aria-label': 'weight',
          }}
        />
      )}
      {error && (
        <FormHelperText error variant="outlined">
          {errorMessage}
        </FormHelperText>
      )}
    </FormControl>
  )
})
